import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";

export default function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const dashboardPath = user && user.role ? (user.role === "admin" ? "/admin" : `/dashboard/${user.role}`) : "/dashboard";

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="header-logo-link">
          <img src="/logo.png" alt="JobVinum" className="h-8 sm:h-9 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
          <Link to="/offers" className="hover:text-[#4ba3b7] transition-colors" data-testid="header-nav-offers-link">
            {t("nav.offers")}
          </Link>
          <Link to="/candidates" className="hover:text-[#4ba3b7] transition-colors" data-testid="header-nav-candidates-link">
            {t("nav.candidates")}
          </Link>
          <Link to="/blog" className="hover:text-[#4ba3b7] transition-colors" data-testid="header-nav-blog-link">
            {t("nav.blog")}
          </Link>
          <Link to="/roadmap" className="hover:text-[#4ba3b7] transition-colors" data-testid="header-nav-roadmap-link">
            {t("nav.roadmap")}
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <>
              <Button asChild variant="outline" size="sm" data-testid="header-dashboard-link">
                <Link to={dashboardPath}>{t("nav.dashboard")}</Link>
              </Button>
              <Button size="sm" onClick={handleLogout} className="bg-[#4ba3b7] hover:bg-[#3D8A9A]" data-testid="header-logout-button">
                {t("nav.logout")}
              </Button>
            </>
          ) : user === false ? (
            <>
              <Button asChild variant="outline" size="sm" data-testid="header-login-link">
                <Link to="/login">{t("nav.login")}</Link>
              </Button>
              <Button asChild size="sm" className="bg-[#4ba3b7] hover:bg-[#3D8A9A]" data-testid="header-register-link">
                <Link to="/register">{t("nav.register")}</Link>
              </Button>
            </>
          ) : null}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="header-mobile-menu-toggle"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-black/5 bg-white px-6 py-4 flex flex-col gap-3">
          <Link to="/offers" onClick={() => setMobileOpen(false)} data-testid="header-mobile-offers-link">{t("nav.offers")}</Link>
          <Link to="/candidates" onClick={() => setMobileOpen(false)} data-testid="header-mobile-candidates-link">{t("nav.candidates")}</Link>
          <Link to="/blog" onClick={() => setMobileOpen(false)} data-testid="header-mobile-blog-link">{t("nav.blog")}</Link>
          <Link to="/roadmap" onClick={() => setMobileOpen(false)} data-testid="header-mobile-roadmap-link">{t("nav.roadmap")}</Link>
          <div className="flex items-center justify-between pt-2">
            <LanguageSwitcher />
          </div>
          {user ? (
            <div className="flex flex-col gap-2 pt-2">
              <Button asChild variant="outline" data-testid="header-mobile-dashboard-link">
                <Link to={dashboardPath} onClick={() => setMobileOpen(false)}>{t("nav.dashboard")}</Link>
              </Button>
              <Button onClick={handleLogout} className="bg-[#4ba3b7]" data-testid="header-mobile-logout-button">
                {t("nav.logout")}
              </Button>
            </div>
          ) : user === false ? (
            <div className="flex flex-col gap-2 pt-2">
              <Button asChild variant="outline" data-testid="header-mobile-login-link">
                <Link to="/login" onClick={() => setMobileOpen(false)}>{t("nav.login")}</Link>
              </Button>
              <Button asChild className="bg-[#4ba3b7]" data-testid="header-mobile-register-link">
                <Link to="/register" onClick={() => setMobileOpen(false)}>{t("nav.register")}</Link>
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </header>
  );
}
